/**
 * MONSTER TOWN — TOWN LAYOUT DATA. OWNED BY THE WORLD CREW (src/world/**).
 *
 * THE SHAPE OF THE PLACE, as numbers. Nothing here builds anything; `terrain.ts`
 * reads this and `buildings.ts` turns a spec into a building. Adding a venue to
 * the street is a row in `BUILDINGS` — no engine change, which is the litmus the
 * brief sets for content.
 *
 * THE PLAN — a town square. -Z is NORTH.
 *
 *   +--------------- the 24x24 grid (48x48 units) ----------------+
 *   |                     [ HOUSE ]                               |
 *   |   ....................ring road......................       |
 *   |   .   +-------------------------------------+      .        |
 *   | [POND].|      YOUR YARD  (fenced, 4 gates)  |      . [SHOP] |
 *   |   .   +-------------------------------------+      .        |
 *   |   ..................................................        |
 *   |        [ ICE CREAM ]          [ RESTAURANT ]                |
 *   +-------------------------------------------------------------+
 *      cottages, tree line and hills sit OUTSIDE the grid — scenery only
 *
 * WHY EVERYTHING WALKABLE IS INSIDE THE GRID: the monster's walker collides
 * against the scene `Grid` and treats out-of-bounds as solid, so a street built
 * outside the grid is a street the monster can never reach. The town square
 * therefore fits inside the plot, and only pure scenery lives beyond it. The
 * grid spec itself is untouched (24x24), so every other crew's cell coordinates
 * still mean exactly what they meant before.
 *
 * The kid can technically build on the road as well as in the yard. That is
 * deliberate — the brief says the street layout is theirs — and the fenced yard
 * is what tells them where "their" bit is.
 *
 * ---------------------------------------------------------------------------
 * THE COMMITTED TOWN PALETTE (brief: "commit one palette across world, UI and
 * characters"). Round-1 critic counted FIVE roof colours and THREE wall colours
 * in a single frame, which is why the town read as a colour swatch rather than
 * a place. The town is now allowed exactly:
 *
 *   ROOFS  roofRed  roofTeal  roofMustard          (roofPurple is not a roof)
 *   WALLS  wallCream  wallMint  wallPink           (pale, all warm-leaning)
 *   TRIM   woodDark  woodLight  wallCream
 *   ACCENT flowerPink / flowerYellow / flowerPurple on planters and awnings
 *
 * Purple and blue stay in the game — as flowers, awning stripes, monsters and
 * interiors — but never as a roof or a wall out here, because those are the two
 * biggest colour areas on screen and they are what set the key.
 *
 * A repeated roof colour is fine as long as the two buildings are on OPPOSITE
 * sides of the plot (house north / restaurant south), so no single frame shows
 * the same roof twice.
 * ---------------------------------------------------------------------------
 */

import type { PaletteName } from '../core/palette'
import type { BuildingSpec } from './buildings'

// ---------------------------------------------------------------------------
// rings (world units; the grid runs -24..+24 on both axes)
// ---------------------------------------------------------------------------

/** Edge of the whole walkable world = the edge of the grid. */
export const GRID_HALF = 24
/** Half-width of the kid's fenced yard. */
export const YARD_HALF = 13
/** Pavement runs from the fence out to the kerb. */
export const WALK_IN = 13
/**
 * Road surface.
 *
 * THE VERGE IS 1.85 UNITS WIDE NOW, NOT 1.05. Round 2: "the postbox and a tree
 * are planted on the tarmac", plus benches, lamp posts and signboards crowding
 * the kerb. They were all inside the pavement band as written — the band was
 * simply too narrow to hold a 1.0-deep bench, a lamp post and a 2.7-wide tree
 * crown without something hanging over the road. The road gives up 0.8 of its
 * width (it is still 3.6 across, ample for this town at this scale) and the
 * pavement takes it.
 */
export const ROAD_IN = 15.2
export const ROAD_OUT = 18.8
/** Pavement on the far side of the road; building fronts sit on its outer edge. */
export const WALK_OUT = 20
/** Front faces of every building on the street. */
export const FRONT = 20
/** Beyond this is scenery a monster never walks into. */
export const TOWN_HALF = GRID_HALF

/** Half-width of the gate openings in the yard fence, on all four sides. */
export const GATE_HALF = 2.6

// ---------------------------------------------------------------------------
// buildings — add a row, get a building. No engine change.
// ---------------------------------------------------------------------------

const HOUSE_D = 5.4
const SHOP_D = 5
const VENUE_D = 4.6

export const BUILDINGS: readonly BuildingSpec[] = [
  {
    id: 'house',
    devName: 'Your house',
    cx: 0,
    cz: -(FRONT + HOUSE_D / 2),
    w: 12,
    d: HOUSE_D,
    h: 3.6,
    front: 'S',
    wall: 'wallCream',
    trim: 'woodDark',
    roof: 'roofRed',
    roofStyle: 'gable',
    sign: 'home',
    chimney: true,
    planterColor: 'flowerPink',
    toScene: 'house',
  },
  {
    id: 'shop',
    devName: 'Town shop',
    cx: FRONT + SHOP_D / 2,
    cz: 0,
    w: 11,
    d: SHOP_D,
    h: 3.4,
    front: 'W',
    wall: 'wallMint',
    trim: 'woodLight',
    roof: 'roofTeal',
    roofStyle: 'flat',
    awning: ['roofTeal', 'wallCream'],
    sign: 'coin',
    planterColor: 'flowerYellow',
    /*
     * THE DOOR OPENS THE ROOM, NOT THE PANEL.
     *
     * This said `panel: 'shop'`, and `hotspots.trigger()` returns as soon as it
     * has emitted `ui:panel` — so tapping the shop's front door opened the flat
     * catalogue sheet and the walk-in shop room that the economy crew builds,
     * registers and restocks (`src/economy/shop.ts`) was unreachable by any tap
     * in the game. Their own file says "IT DOES NOT REPLACE THE WALK-IN SHOP.
     * That room is the nicer experience and stays." It had not stayed.
     *
     * The door now walks the monster inside. The catalogue panel is still one
     * tap away on the HUD's bag button, and the shelves in the room open it too,
     * so nothing a kid could reach before has been taken away.
     */
    toScene: 'shop',
  },
  {
    id: 'iceCream',
    devName: 'Ice cream shop',
    cx: -8,
    cz: FRONT + VENUE_D / 2,
    w: 9,
    d: VENUE_D,
    h: 3.3,
    front: 'N',
    // Committed palette: pale pink walls, mustard roof, cream stripes. The old
    // purple roof was the single loudest thing in the town-wide frame.
    wall: 'wallPink',
    trim: 'wallCream',
    roof: 'roofMustard',
    // Gable, not flat: from a fixed iso camera a flat roof is a huge blank
    // slab, and a pitched one reads as a building at a glance.
    roofStyle: 'gable',
    awning: ['flowerPink', 'wallCream'],
    sign: 'cone',
    planterColor: 'flowerPurple',
    // The venue crew's ice cream room. THE ID IS A CROSS-CREW CONTRACT and it
    // is documented in `src/content/menu.ts`: the registered scenes are
    // `venue_iceCream` / `venue_restaurant` / `shop`. This row said `venue1`
    // for a whole round — a scene that has never existed — so `trigger()` in
    // hotspots.ts knocked, bounced, chimed and toasted and then dropped the
    // transition on the floor, and the headline v1 venue was unreachable by any
    // tap a child could make. Nothing failed loudly, which is why it survived.
    // `checkDoors()` in town.ts now shouts on boot and `npm run drive` gates on
    // it, so a door into a scene that does not exist can never be quiet again.
    toScene: 'venue_iceCream',
  },
  {
    id: 'restaurant',
    devName: 'Restaurant',
    cx: 8,
    cz: FRONT + VENUE_D / 2,
    w: 9,
    d: VENUE_D,
    h: 3.4,
    front: 'N',
    // Cream walls + a red roof, mirroring the house on the far side of the
    // plot. Same roof colour, opposite side — never both in one frame.
    wall: 'wallCream',
    trim: 'woodDark',
    roof: 'roofRed',
    roofStyle: 'gable',
    awning: ['roofRed', 'wallCream'],
    sign: 'plate',
    planterColor: 'flowerRed',
    // The venue crew's restaurant room. If a venue scene is ever missing, the
    // door still knocks, bounces and chimes — never a dead tap.
    toScene: 'venue_restaurant',
  },
]

// ---------------------------------------------------------------------------
// pond (the west park) — its shore is inside the grid, its far side is not
// ---------------------------------------------------------------------------

/**
 * Pushed WEST out of the pavement. It used to span x -27.5..-19.5 with a sand
 * shore to -18.1, so the shore ran straight under the kerb and the water butted
 * into the road on a hard straight edge — the "swimming pool" read. Now the
 * shore stops flush with the outer pavement and the water never touches tarmac.
 */
export const POND = {
  cx: -25,
  cz: 0,
  /** Half sizes of the OPEN WATER; the shore adds ~1.6 more all round. */
  hx: 3.4,
  hz: 6.4,
} as const

// ---------------------------------------------------------------------------
// scenery (all of this lives OUTSIDE the grid — nobody ever walks to it)
// ---------------------------------------------------------------------------

/** Small closed cottages that give the street depth. Never enterable. */
export const COTTAGES: readonly {
  x: number
  z: number
  wall: PaletteName
  roof: PaletteName
  yaw: number
}[] = [
  // NO GREEN-ISH WALLS OUT HERE. The scenery cottages sit against the fogged
  // green tree line and hills, and they lost their ink outlines to make the
  // older-iPad perf budget — so a pale mint wall (`wallMint` #CFEFD8) has almost
  // nothing left to separate it from the background. At the widest zoom that
  // cottage's body disappeared completely and its roof and window hung in the
  // air over the grass looking like a rendering bug. Cool/warm walls keep their
  // silhouette without costing a draw call (LAW #5).
  // Committed palette, and only the two warm walls out here (see the header):
  // cream and pink both hold a silhouette against the fogged green tree line.
  { x: 30, z: -28, wall: 'wallCream', roof: 'roofTeal', yaw: -2.3 },
  { x: -29, z: 29, wall: 'wallPink', roof: 'roofRed', yaw: 0.8 },
  { x: 30, z: 28, wall: 'wallCream', roof: 'roofMustard', yaw: 2.5 },
  { x: -30, z: -27, wall: 'wallPink', roof: 'roofTeal', yaw: -0.7 },
]

/** Inner and outer radius of the belt of trees and bushes around the town. */
export const GREEN_BELT_IN = 25.5
export const GREEN_BELT_OUT = 38
